import { describeRecurrence } from "@estelle/domain";
import type { Project, RecurringTask, Task } from "@estelle/domain";

import { reportingNow } from "@/lib/reportClock";

/** A row flattened to the field names its module declares. */
export type Projection = Record<string, unknown>;

const CLOSED_STATUSES = new Set<Task["status"]>(["done", "cancelled"]);

/**
 * FR-TSK-16's overdue filter: a due date in the past on a task nobody has
 * closed. Checked against `reportingNow()`: on fixtures that is the fixed
 * in-world clock rather than `Date.now()`, so a task that was overdue when
 * this screen was written stays overdue in a screenshot taken next week
 * instead of quietly becoming current — the fixture data was generated
 * against this same instant, so the two only agree if both use it. Against
 * the API it is the real clock, which is the only one a live due date means
 * anything against.
 */
export function isOverdue(task: Pick<Task, "dueDate" | "status">): boolean {
  if (task.dueDate === null) return false;
  if (CLOSED_STATUSES.has(task.status)) return false;
  return Date.parse(task.dueDate) < reportingNow();
}

/**
 * A task flattened for display and for the client-side filtering
 * `TaskList.tsx` and `TaskBoard.tsx` do — see the comment on `overdue` in
 * `fields.ts` for why filtering happens here rather than at
 * `TasksRepository.list`.
 *
 * Deliberately computed from the row and nothing else. Read the comment at
 * the top of `modules/orders/projections.ts`; it explains why and applies
 * here unchanged. `assigneeUserId`, `reporterUserId` and `projectId` stay
 * ids rather than names because `Task` carries no denormalised name for any
 * of the three — `TaskList.tsx` resolves them to names for display via a
 * small directory query, the same way `TransfersList` resolves a warehouse
 * id to a name, which this function has no business doing for itself.
 *
 * `linkedEntityType` from `TASK_FIELDS` has no entry below: `Task` (unlike
 * `TaskWithDetails`) carries no `links` array, so there is nothing in the
 * row for this to read.
 */
export function projectTask(task: Task): Projection {
  return {
    reference: task.reference,
    title: task.title,
    status: task.status,
    priority: task.priority,
    assigneeUserId: task.assigneeUserId ?? "",
    dueDate: task.dueDate ?? "",
    projectId: task.projectId,
    reporterUserId: task.reporterUserId,
    tags: task.tags,
    overdue: isOverdue(task),
  };
}

/** A project flattened for display. Computed from the row alone, for the same reason. */
export function projectProject(project: Project): Projection {
  return {
    name: project.name,
    key: project.key,
    ownerUserId: project.ownerUserId,
    description: project.description ?? "",
    isArchived: project.isArchived,
    createdAt: project.createdAt,
  };
}

/**
 * A schedule flattened for the Recurring list (#624), from the row alone: the
 * sentence for `repeats` is computed from the rule it carries, and a paused
 * schedule's `nextDate` is empty because the API says it has none.
 */
export function projectRecurring(recurring: RecurringTask): Projection {
  return {
    title: recurring.title,
    repeats: describeRecurrence(recurring),
    assigneeUserId: recurring.assigneeUserId,
    nextDate: recurring.nextDate ?? "",
    projectId: recurring.projectId,
    isActive: recurring.isActive,
    priority: recurring.priority,
    lastProblem: recurring.lastProblem ?? "",
  };
}
